import type { Job } from "../../../../../api/types";
import type {
	WizardState,
	WizardAction,
	WizardStep,
	WizardPhase,
	WizardIssues,
	WizardConfigurationState,
	OverlapIssue,
	DuplicateIssue,
	UnmatchedIssue,
	InvalidDataIssue,
	ConfigurationStep,
	ValidationStep,
} from "../WizardContainer.types";
import { CONFIGURATION_STEPS, VALIDATION_STEPS, WIZARD_STEPS } from "../WizardContainer.types";

// ============================================================================
// Constants
// ============================================================================

// All steps in order (configuration then validation)
const STEP_ORDER: WizardStep[] = WIZARD_STEPS;

// ============================================================================
// Initial State Factory
// ============================================================================

/**
 * Creates an empty configuration state
 */
export function createEmptyConfigurationState(): WizardConfigurationState {
	return {
		warnings: [],
		uploadedFiles: [],
		geodataGroups: [],
		propertyMappings: {},
		geoTypeSelections: {},
		excludedGroupIds: new Set(),
		selectedGroupIdsForMap: [],
		geoDataMap: new Map(),
		selectableGeoData: {},
		originalFiles: [],
	};
}

/**
 * Creates the initial wizard state with the provided jobs (validation phase)
 */
export function createInitialWizardState(jobs: Job[]): WizardState {
	return {
		phase: "validation",
		configuration: createEmptyConfigurationState(),
		jobs,
		currentStep: "duplicate-features", // Start with duplicates (first validation step)
		currentIssueIndex: 0,
		issues: {
			overlaps: [],
			duplicates: [],
			unmatched: [],
			invalidData: [],
		},
		skippedIssueIds: new Set(),
		emptySteps: new Set(),
		completedSteps: new Set(),
		stepsWithInitialIssues: new Set(),
		isValidating: false,
		validationError: null,
	};
}

/**
 * Creates the initial wizard state for configuration phase (with geodata)
 */
export function createInitialConfigurationState(configuration: WizardConfigurationState): WizardState {
	return {
		phase: "configuration",
		configuration,
		jobs: [],
		currentStep: "file-validation", // Start at first configuration step
		currentIssueIndex: 0,
		issues: {
			overlaps: [],
			duplicates: [],
			unmatched: [],
			invalidData: [],
		},
		skippedIssueIds: new Set(),
		emptySteps: new Set(),
		completedSteps: new Set(),
		stepsWithInitialIssues: new Set(),
		isValidating: false,
		validationError: null,
	};
}

// ============================================================================
// Helper: Determine first step with issues from issues object
// ============================================================================

/**
 * Gets the first step that has issues, based on issues object
 * Used when SET_ISSUES is dispatched to navigate to the correct step
 */
function getFirstStepWithIssuesFromIssues(issues: WizardIssues): WizardStep {
	// Check in STEP_ORDER order
	if (issues.duplicates.length > 0) return "duplicate-features";
	if (issues.overlaps.length > 0) return "overlapping-boundaries";
	if (issues.unmatched.length > 0) return "unmatched-features";
	if (issues.invalidData.length > 0) return "missing-invalid-data";
	return "summary";
}

// ============================================================================
// Reducer
// ============================================================================

/**
 * Wizard state reducer - handles all state transitions
 */
export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		// ============================================================================
		// Configuration Phase Actions
		// ============================================================================

		case "SET_CONFIGURATION":
			return {
				...state,
				phase: "configuration",
				configuration: action.configuration,
				currentStep: "file-validation",
			};

		case "UPDATE_GEODATA":
			return {
				...state,
				configuration: {
					...state.configuration,
					...action.geodata,
				},
			};

		case "CLEAR_GEODATA":
			return {
				...state,
				// Reset to configuration phase
				phase: "configuration",
				// Clear jobs
				jobs: [],
				// Reset issues
				issues: {
					overlaps: [],
					duplicates: [],
					unmatched: [],
					invalidData: [],
				},
				// Clear skipped issues
				skippedIssueIds: new Set(),
				// Reset completed steps and initial issue tracking
				completedSteps: new Set(),
				stepsWithInitialIssues: new Set(),
				// Reset issue index
				currentIssueIndex: 0,
				// Clear configuration
				configuration: {
					...state.configuration,
					warnings: [],
					uploadedFiles: [],
					geodataGroups: [],
					propertyMappings: {},
					geoTypeSelections: {},
					excludedGroupIds: new Set(),
					selectedGroupIdsForMap: [],
					geoDataMap: new Map(),
					selectableGeoData: {},
					originalFiles: [],
				},
			};

		case "UPDATE_PROPERTY_MAPPINGS":
			return {
				...state,
				configuration: {
					...state.configuration,
					propertyMappings: {
						...state.configuration.propertyMappings,
						[action.groupId]: action.mappings,
					},
				},
			};

		case "UPDATE_ALL_PROPERTY_MAPPINGS":
			return {
				...state,
				configuration: {
					...state.configuration,
					propertyMappings: action.mappings,
				},
			};

		case "UPDATE_GEO_TYPE":
			return {
				...state,
				configuration: {
					...state.configuration,
					geoTypeSelections: {
						...state.configuration.geoTypeSelections,
						[action.groupId]: action.geoType,
					},
				},
			};

		case "UPDATE_ALL_GEO_TYPES":
			return {
				...state,
				configuration: {
					...state.configuration,
					geoTypeSelections: {
						...state.configuration.geoTypeSelections,
						...action.updates,
					},
				},
			};

		case "EXCLUDE_GROUP": {
			const newExcluded = new Set(state.configuration.excludedGroupIds);
			newExcluded.add(action.groupId);
			// Also remove from map selection
			const newSelectedForMap = state.configuration.selectedGroupIdsForMap.filter((id) => id !== action.groupId);
			return {
				...state,
				configuration: {
					...state.configuration,
					excludedGroupIds: newExcluded,
					selectedGroupIdsForMap: newSelectedForMap,
				},
			};
		}

		case "INCLUDE_GROUP": {
			const newExcluded = new Set(state.configuration.excludedGroupIds);
			newExcluded.delete(action.groupId);
			return {
				...state,
				configuration: {
					...state.configuration,
					excludedGroupIds: newExcluded,
				},
			};
		}

		case "SET_SELECTED_GROUPS_FOR_MAP":
			return {
				...state,
				configuration: {
					...state.configuration,
					selectedGroupIdsForMap: action.groupIds,
				},
			};

		case "COMPLETE_CONFIGURATION": {
			// Transition from configuration to validation phase
			const newCompleted = new Set(state.completedSteps);
			// Mark all configuration steps as completed
			CONFIGURATION_STEPS.forEach((step) => newCompleted.add(step));

			return {
				...state,
				phase: "validation" as WizardPhase,
				jobs: action.jobs,
				currentStep: "duplicate-features" as WizardStep, // First validation step
				currentIssueIndex: 0,
				completedSteps: newCompleted,
				skippedIssueIds: new Set<string>(),
			};
		}

		// ============================================================================
		// Validation Phase Actions
		// ============================================================================

		case "SET_JOBS":
			return {
				...state,
				jobs: action.jobs,
				currentIssueIndex: 0,
				skippedIssueIds: new Set(),
				preserveStepOnNextValidation: action.preserveStep ?? false,
			};

		case "UPDATE_JOB": {
			const jobIndex = state.jobs.findIndex((j) => j.id === action.job.id);
			if (jobIndex === -1) return state;

			const newJobs = [...state.jobs];
			newJobs[jobIndex] = action.job;
			return {
				...state,
				jobs: newJobs,
			};
		}

		case "DELETE_JOB":
			return {
				...state,
				jobs: state.jobs.filter((j) => j.id !== action.jobId),
			};

		case "DELETE_JOBS": {
			const idsToDelete = new Set(action.jobIds);
			return {
				...state,
				jobs: state.jobs.filter((j) => !idsToDelete.has(j.id)),
			};
		}

		case "SKIP_ISSUE": {
			const newSkipped = new Set(state.skippedIssueIds);
			newSkipped.add(action.issueId);
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "UNSKIP_ISSUE": {
			const newSkipped = new Set(state.skippedIssueIds);
			newSkipped.delete(action.issueId);
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "SKIP_ALL_STEP_ISSUES": {
			const issues = getIssuesForStep(state, state.currentStep);
			const newSkipped = new Set(state.skippedIssueIds);
			issues.forEach((issue) => newSkipped.add(issue.id));
			return {
				...state,
				skippedIssueIds: newSkipped,
			};
		}

		case "NEXT_STEP": {
			// Skip steps that have no issues (not visible in progress bar)
			const nextStep = getNextVisibleStep(state, state.currentStep);
			if (!nextStep) return state;

			const newCompleted = new Set(state.completedSteps);
			newCompleted.add(state.currentStep);

			return {
				...state,
				currentStep: nextStep,
				currentIssueIndex: 0,
				completedSteps: newCompleted,
			};
		}

		case "PREV_STEP": {
			// Skip steps that have no issues (not visible in progress bar)
			const prevStep = getPreviousVisibleStep(state, state.currentStep);
			if (!prevStep) return state;

			// Get the issue count for the previous step to land on its last issue
			// This enables seamless backward navigation through issues
			const prevStepIssueCount = getStepIssueCount(state, prevStep);
			const lastIssueIndex = Math.max(0, prevStepIssueCount - 1);

			return {
				...state,
				currentStep: prevStep,
				currentIssueIndex: lastIssueIndex,
			};
		}

		case "NEXT_ISSUE": {
			// Bounds check to prevent index from exceeding issue count
			const issues = getIssuesForStep(state, state.currentStep);
			const maxIndex = Math.max(0, issues.length - 1);
			return {
				...state,
				currentIssueIndex: Math.min(state.currentIssueIndex + 1, maxIndex),
			};
		}

		case "PREV_ISSUE":
			return {
				...state,
				currentIssueIndex: Math.max(0, state.currentIssueIndex - 1),
			};

		case "GO_TO_ISSUE":
			return {
				...state,
				currentIssueIndex: action.index,
			};

		case "GO_TO_STEP":
			return {
				...state,
				currentStep: action.step,
				currentIssueIndex: 0,
			};

		case "SET_ISSUES": {
			// Determine which steps currently have issues
			const stepsWithIssuesNow = new Set<WizardStep>();
			if (action.issues.duplicates.length > 0) stepsWithIssuesNow.add("duplicate-features");
			if (action.issues.overlaps.length > 0) stepsWithIssuesNow.add("overlapping-boundaries");
			if (action.issues.unmatched.length > 0) stepsWithIssuesNow.add("unmatched-features");
			if (action.issues.invalidData.length > 0) stepsWithIssuesNow.add("missing-invalid-data");

			// Merge with existing stepsWithInitialIssues (union - append only)
			// Once a step has had issues, it stays in this set for the session
			const updatedInitialIssues = new Set([...state.stepsWithInitialIssues, ...stepsWithIssuesNow]);

			// If preserveStep is true OR if state has preserveStepOnNextValidation flag,
			// keep the current step. This is used when splitting jobs to avoid jumping back to earlier steps.
			const shouldPreserveStep = action.preserveStep || state.preserveStepOnNextValidation;
			if (shouldPreserveStep) {
				return {
					...state,
					issues: action.issues,
					stepsWithInitialIssues: updatedInitialIssues,
					preserveStepOnNextValidation: false, // Clear the flag
				};
			}
			// Otherwise, determine the first step that has issues
			const firstStepWithIssues = getFirstStepWithIssuesFromIssues(action.issues);
			return {
				...state,
				issues: action.issues,
				stepsWithInitialIssues: updatedInitialIssues,
				currentStep: firstStepWithIssues,
				currentIssueIndex: 0,
				preserveStepOnNextValidation: false,
			};
		}

		case "SET_VALIDATING":
			return {
				...state,
				isValidating: action.isValidating,
			};

		case "SET_VALIDATION_ERROR":
			return {
				...state,
				validationError: action.error,
			};

		case "MARK_STEP_COMPLETE": {
			const newCompleted = new Set(state.completedSteps);
			newCompleted.add(action.step);
			return {
				...state,
				completedSteps: newCompleted,
			};
		}

		case "RESET":
			return {
				...createInitialWizardState(state.jobs),
				jobs: state.jobs,
			};

		default:
			return state;
	}
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the next step in the wizard sequence (without visibility check)
 */
export function getNextStep(currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex === -1 || currentIndex >= STEP_ORDER.length - 1) {
		return null;
	}
	return STEP_ORDER[currentIndex + 1];
}

/**
 * Gets the next visible step (skips steps with no issues)
 */
export function getNextVisibleStep(state: WizardState, currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex === -1) return null;

	for (let i = currentIndex + 1; i < STEP_ORDER.length; i++) {
		const step = STEP_ORDER[i];
		if (shouldShowStep(state, step)) {
			return step;
		}
	}
	return null;
}

/**
 * Gets the previous step in the wizard sequence (without visibility check)
 */
export function getPreviousStep(currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex <= 0) {
		return null;
	}
	return STEP_ORDER[currentIndex - 1];
}

/**
 * Gets the previous visible step (skips steps with no issues)
 */
export function getPreviousVisibleStep(state: WizardState, currentStep: WizardStep): WizardStep | null {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	if (currentIndex <= 0) return null;

	for (let i = currentIndex - 1; i >= 0; i--) {
		const step = STEP_ORDER[i];
		if (shouldShowStep(state, step)) {
			return step;
		}
	}
	return null;
}

/**
 * Checks if the user can proceed from the current step
 * (all issues for the current step must be skipped, or configuration step requirements met)
 */
export function canProceedFromStep(state: WizardState): boolean {
	// Configuration step validation
	if (isConfigurationStep(state.currentStep)) {
		return canProceedFromConfigurationStep(state);
	}

	// Validation step validation
	const issueCount = getStepIssueCount(state, state.currentStep);
	if (issueCount === 0) {
		return true;
	}

	const issues = getIssuesForStep(state, state.currentStep);
	return issues.every((issue) => state.skippedIssueIds.has(issue.id));
}

/**
 * Checks if user can proceed from a configuration step
 */
export function canProceedFromConfigurationStep(state: WizardState): boolean {
	switch (state.currentStep) {
		case "file-validation":
			// Can always proceed from file validation (warnings are informational)
			return true;

		case "property-mapping":
			// Can always proceed from property mapping (mappings are optional)
			return true;

		case "geo-type-selection": {
			// Must have all non-excluded groups typed AND at least one boundary
			const { geodataGroups, geoTypeSelections, excludedGroupIds } = state.configuration;

			// Get non-excluded groups
			const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));

			// All active groups must have a geo-type assigned
			const allTyped = activeGroups.every(
				(g) => geoTypeSelections[g.id] !== null && geoTypeSelections[g.id] !== undefined,
			);

			// At least one group must be assigned as Boundary
			const hasBoundary = activeGroups.some((g) => geoTypeSelections[g.id] === "Boundary");

			return allTyped && hasBoundary;
		}

		default:
			return true;
	}
}

/**
 * Checks if a step is a configuration step
 */
export function isConfigurationStep(step: WizardStep): step is ConfigurationStep {
	return CONFIGURATION_STEPS.includes(step as ConfigurationStep);
}

/**
 * Checks if a step is a validation step
 */
export function isValidationStep(step: WizardStep): step is ValidationStep {
	return VALIDATION_STEPS.includes(step as ValidationStep);
}

/**
 * Gets the number of issues for a given step
 */
export function getStepIssueCount(state: WizardState, step: WizardStep): number {
	// Configuration steps don't have "issues" in the same sense
	if (isConfigurationStep(step)) {
		return 0;
	}

	switch (step) {
		case "overlapping-boundaries":
			return state.issues.overlaps.length;
		case "duplicate-features":
			return state.issues.duplicates.length;
		case "unmatched-features":
			return state.issues.unmatched.length;
		case "missing-invalid-data":
			return state.issues.invalidData.length;
		case "summary":
			return 0;
		default:
			return 0;
	}
}

/**
 * Gets the issues array for a given step
 */
export function getIssuesForStep(
	state: WizardState,
	step: WizardStep,
): (OverlapIssue | DuplicateIssue | UnmatchedIssue | InvalidDataIssue)[] {
	switch (step) {
		case "overlapping-boundaries":
			return state.issues.overlaps;
		case "duplicate-features":
			return state.issues.duplicates;
		case "unmatched-features":
			return state.issues.unmatched;
		case "missing-invalid-data":
			return state.issues.invalidData;
		case "summary":
			return [];
		default:
			return [];
	}
}

/**
 * Gets the number of unskipped issues for the current step
 */
export function getUnskippedIssueCount(state: WizardState): number {
	const issues = getIssuesForStep(state, state.currentStep);
	return issues.filter((issue) => !state.skippedIssueIds.has(issue.id)).length;
}

/**
 * Determines if a step should be shown based on phase and whether it has issues
 * OR if it had issues initially (to show resolved state with checkmark)
 */
export function shouldShowStep(state: WizardState, step: WizardStep): boolean {
	// Configuration steps are always shown when in configuration phase
	// or when they have been completed (to show in progress bar)
	if (isConfigurationStep(step)) {
		// Always show in configuration phase
		if (state.phase === "configuration") {
			return true;
		}
		// Show if completed (user has progressed past it)
		if (state.completedSteps.has(step)) {
			return true;
		}
		// Don't show configuration steps in validation phase if not completed
		return false;
	}

	// Summary always shows (in validation phase)
	if (step === "summary") {
		// Only show summary in validation phase
		return state.phase === "validation";
	}

	// Validation steps - only show in validation phase
	if (state.phase !== "validation") {
		return false;
	}

	// Show if step currently has issues
	if (getStepIssueCount(state, step) > 0) {
		return true;
	}

	// Show if step had issues initially (even if now resolved)
	if (state.stepsWithInitialIssues.has(step)) {
		return true;
	}

	return false;
}

/**
 * Determines if a step is resolved (had issues initially, but now has no unskipped issues)
 * Used to show green checkmarks for steps that were fixed or had all issues skipped
 */
export function isStepResolved(state: WizardState, step: WizardStep): boolean {
	// Only steps that initially had issues can be "resolved"
	if (!state.stepsWithInitialIssues.has(step)) {
		return false;
	}

	// Check if current issue count is zero (resolved by fixing)
	const currentIssueCount = getStepIssueCount(state, step);
	if (currentIssueCount === 0) {
		return true;
	}

	// Check if all issues are skipped (resolved by skipping)
	const issues = getIssuesForStep(state, step);
	const allSkipped = issues.every((issue) => state.skippedIssueIds.has(issue.id));
	return allSkipped;
}

/**
 * Gets the first step that has issues (for navigation after validation)
 */
export function getFirstStepWithIssues(state: WizardState): WizardStep {
	for (const step of STEP_ORDER) {
		if (step === "summary") continue;
		if (getStepIssueCount(state, step) > 0) {
			return step;
		}
	}
	return "summary";
}
